import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { supabase, Profile } from "../../../../lib/supabase";
import { useApp } from "../../../../contexts/AppContext";

export default function FollowedProviders() {
  const { currentUser, toggleFollow, followedProviders } = useApp();
  const [providers, setProviders] = useState<Profile[]>([]);
  const [updates, setUpdates] = useState<{ id: string; provider_id: string; provider_name: string; title: string; created_at: string; type: string }[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!currentUser?.id) { setLoading(false); return; }
    const load = async () => {
      const ids = Array.from(followedProviders);
      if (ids.length === 0) { setLoading(false); return; }

      const { data: profiles } = await supabase
        .from("profiles")
        .select("*")
        .in("id", ids);

      setProviders(profiles || []);

      // Load recent products from followed providers
      const { data: recentProducts } = await supabase
        .from("products")
        .select("id, title, type, provider_id, created_at")
        .in("provider_id", ids)
        .eq("visibility", "public")
        .order("created_at", { ascending: false })
        .limit(10);

      if (recentProducts && profiles) {
        const profileMap = Object.fromEntries(profiles.map((p) => [p.id, p.service_name || p.name]));
        const sevenDaysAgo = Date.now() - 7 * 24 * 60 * 60 * 1000;
        setUpdates(
          recentProducts
            .filter((p) => new Date(p.created_at).getTime() > sevenDaysAgo)
            .map((p) => ({
              id: p.id,
              provider_id: p.provider_id,
              provider_name: profileMap[p.provider_id] ?? "事業者",
              title: p.title,
              created_at: p.created_at,
              type: p.type,
            }))
        );
      }
      setLoading(false);
    };
    load();
  }, [currentUser?.id, followedProviders]);

  const getAvatar = (p: Profile) =>
    p.avatar_url ||
    `https://public.readdy.ai/ai/img_res/c76d527685ff1d0b35807a2697167764.jpg${p.id.slice(0, 8)}&orientation=squarish`;

  const formatDate = (iso: string) => {
    const d = new Date(iso);
    const diff = Date.now() - d.getTime();
    if (diff < 86400000) return "本日";
    const days = Math.floor(diff / 86400000);
    return `${days}日前`;
  };

  if (loading) {
    return (
      <div className="space-y-4">
        {[...Array(3)].map((_, i) => (
          <div key={i} className="bg-white rounded-2xl h-20 animate-pulse border border-stone-100" />
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {updates.length > 0 && (
        <div className="bg-teal/5 border border-teal/20 rounded-2xl p-5">
          <p className="font-serif text-teal text-sm mb-3 flex items-center gap-2">
            <i className="ri-notification-3-line" /> フォロー中事業者の新着情報
          </p>
          <div className="space-y-3">
            {updates.slice(0, 5).map((u) => (
              <div key={u.id} className="flex items-start gap-3 bg-white rounded-xl p-3">
                <div className="w-8 h-8 flex items-center justify-center bg-teal/10 rounded-full flex-shrink-0">
                  <i className={u.type === "video" ? "ri-play-circle-line text-teal" : "ri-box-3-line text-gold"} />
                </div>
                <div>
                  <p className="text-xs font-sans text-teal font-medium">{u.provider_name}</p>
                  <p className="text-sm font-sans text-charcoal">「{u.title}」を公開しました</p>
                  <p className="text-xs text-charcoal/40 font-sans mt-1">{formatDate(u.created_at)}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <h3 className="font-serif text-deep-green text-lg">
        フォロー中の事業者 <span className="text-sm font-sans text-charcoal/40">({providers.length})</span>
      </h3>

      {providers.length === 0 ? (
        <div className="text-center py-20 bg-white rounded-2xl border border-stone-100">
          <i className="ri-user-follow-line text-5xl text-charcoal/20 block mb-4" />
          <p className="text-charcoal/40 font-sans text-sm">まだ誰もフォローしていません</p>
          <Link to="/providers" className="mt-4 inline-block text-teal text-sm font-sans cursor-pointer hover:underline">
            事業者を探す →
          </Link>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {providers.map((p) => (
            <div key={p.id} className="bg-white rounded-2xl p-5 border border-stone-100 flex gap-4 items-start">
              <div className="w-14 h-14 rounded-full overflow-hidden flex-shrink-0">
                <img src={getAvatar(p)} alt={p.name} className="w-full h-full object-cover object-top" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-serif text-charcoal text-sm">{p.service_name || p.name}</p>
                <p className="text-xs text-charcoal/50 font-sans">{p.name} · {p.location || "日本"}</p>
                <div className="flex gap-2 mt-2 flex-wrap">
                  {(p.skills || []).slice(0, 2).map((s) => (
                    <span key={s} className="text-xs bg-ivory px-2 py-0.5 rounded-full text-charcoal/60 font-sans">{s}</span>
                  ))}
                </div>
              </div>
              <div className="flex flex-col items-end gap-2 flex-shrink-0">
                <button
                  onClick={() => toggleFollow(p.id)}
                  className="text-xs border border-teal text-teal px-3 py-1.5 rounded-full whitespace-nowrap cursor-pointer hover:bg-teal hover:text-white transition-colors font-sans"
                >
                  フォロー中
                </button>
                <Link
                  to={`/providers/${p.id}`}
                  className="text-xs text-charcoal/50 font-sans cursor-pointer hover:text-teal whitespace-nowrap"
                >
                  詳細 →
                </Link>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
